import { useCallback, useEffect, useRef } from 'react';

import { company } from '~/company/Company';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import type { ProductSnippetItem } from '~/services/analytics/events';
import type { ProductListDescriptor } from '~/services/analytics/productLists';
import { cartStore } from '~/stores';
import type { Offer, Product } from '~/stores/CategoriesStore';

import { useProductListAnalyticsContext } from './ProductListAnalyticsContext';

const SNIPPET_VISIBILITY_THRESHOLD = 0.5;

const toSnippetItem = (
  item: Product | Offer,
  list: ProductListDescriptor,
  index?: number,
): ProductSnippetItem => {
  const snippet = cartStore.convertToFirebase(item, index ?? 0);

  return {
    ...snippet,
    currency: company.currencyCode,
    item_category: snippet.item_category || list.category || '',
    item_list_id: list.id,
    item_list_name: list.name,
  };
};

/**
 * Reports the card as a snippet of the surrounding product list. Outside a
 * `ProductListAnalyticsProvider` every call is a no-op.
 */
export const useProductSnippetAnalytics = (
  item: Product | Offer,
  index?: number,
) => {
  const list = useProductListAnalyticsContext();
  const snippetRef = useRef<HTMLDivElement>(null);
  const itemKey = (item as Offer).sku || String(item.id);

  useEffect(() => {
    const element = snippetRef.current;
    if (!list || !element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        observer.disconnect();
        analyticsService.addToBatch(
          AnalyticsEvent.CATALOG_PRODUCT_SNIPPET_SHOWN,
          toSnippetItem(item, list, index),
        );
      },
      { threshold: SNIPPET_VISIBILITY_THRESHOLD },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [list?.id, itemKey, index]);

  const trackSnippetClick = useCallback(() => {
    if (!list) {
      return;
    }
    analyticsService.track(AnalyticsEvent.PRODUCT_SNIPPET_CLICKED, {
      item_list_id: list.id,
      item_list_name: list.name,
      items: [toSnippetItem(item, list, index)],
    });
  }, [list, item, index]);

  return { snippetRef, trackSnippetClick };
};
